import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, memoryLocalCache } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

const dbId = firebaseConfig.firestoreDatabaseId && firebaseConfig.firestoreDatabaseId !== '(default)'
  ? firebaseConfig.firestoreDatabaseId
  : undefined;

let dbInstance;

if (typeof window !== 'undefined') {
  try {
    // Using memoryLocalCache to completely prevent persistent tab manager and IndexedDB lock crash bugs,
    // ensuring 100% deterministic, ultra-fast online-authoritative synchronization without stale e-commerce data.
    dbInstance = initializeFirestore(app, {
      localCache: memoryLocalCache()
    }, dbId);
  } catch (error) {
    console.warn("Firestore initialization with memoryLocalCache failed. Falling back to default getFirestore.", error);
    try {
      dbInstance = getFirestore(app, dbId);
    } catch (fallbackError) {
      console.error("Fatal: Both initializeFirestore and getFirestore failed.", fallbackError);
      dbInstance = getFirestore(app);
    }
  }
} else {
  dbInstance = getFirestore(app, dbId);
}

export const db = dbInstance;

export const auth = getAuth(app);
if (typeof window !== 'undefined') {
  setPersistence(auth, browserLocalPersistence).catch(console.error);
}
export const storage = getStorage(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map((provider) => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

/**
 * Sanitizes data for Firestore by converting all `undefined` values to `null`
 * or empty strings, avoiding "Function setDoc() called with invalid data. Unsupported field value: undefined" errors.
 */
export function sanitizeFirestoreData<T>(obj: T): T {
  if (obj === undefined) {
    return null as unknown as T;
  }
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeFirestoreData(item)) as unknown as T;
  }

  // Check if obj is a plain object or special Firestore FieldValue / object
  const isPlainObject = obj.constructor === Object || !obj.constructor;
  if (!isPlainObject) {
    return obj;
  }

  const cleanObj: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj as Record<string, any>)) {
    if (value === undefined) {
      cleanObj[key] = null;
    } else if (value !== null && typeof value === 'object') {
      cleanObj[key] = sanitizeFirestoreData(value);
    } else {
      cleanObj[key] = value;
    }
  }
  return cleanObj as T;
}

